import { ApiPage, emptyApiPage } from "../models/api-page.model";
import { Fee } from "../models/fee.model";
import { ServiceOwner } from "../models/service.model";
import { apiService } from "./api.service";

export class FeeService {
  private static BASE_PATH = "/api/v1";
  private static FEES_PATH = `${this.BASE_PATH}/fees`;
  private static OFFICES_PATH = `${this.BASE_PATH}/offices`;

  /*
    SYSTEM FEES
  */
  static async searchSystemFees(term: string, page = 0, size = 20, sort = "name,asc"): Promise<ApiPage<Fee>> {
    const response = await apiService.post(`${this.FEES_PATH}/search?page=${page}&size=${size}&sort=${sort}`, {
      term: term,
    });
    if (!response.success) return emptyApiPage<Fee>();

    return response.data as ApiPage<Fee>;
  }

  // Returns saved fee with ID included, or null on failure
  static async addSystemFee(fee: Fee): Promise<Fee | null> {
    const response = await apiService.post(`${this.FEES_PATH}`, fee);
    if (!response.success) return null;

    return response.data as Fee;
  }

  static async updateSystemFee(fee: Fee): Promise<Fee | null> {
    const response = await apiService.put(`${this.FEES_PATH}/${fee.feeId}`, fee);
    if (!response.success) return null;

    return response.data as Fee;
  }

  static async deleteSystemFee(feeId: string): Promise<boolean> {
    const response = await apiService.delete(`${this.FEES_PATH}/${feeId}`);
    if (!response.success) return false;

    return true;
  }

  /*
    OFFICE FEES
  */

  static async searchOfficeFees(
    officeId: string,
    term: string,
    page = 0,
    size = 20,
    sort = "name,asc",
    feeOwner = ServiceOwner.Office
  ): Promise<ApiPage<Fee>> {
    const response = await apiService.post(`${this.OFFICES_PATH}/${officeId}/fees/search?page=${page}&size=${size}&sort=${sort}`, {
      term: term,
      feeOwner,
    });
    if (!response.success) return emptyApiPage<Fee>();

    return response.data as ApiPage<Fee>;
  }

  static async addOfficeFee(officeId: string, fee: Fee): Promise<Fee | null> {
    const response = await apiService.post(`${this.OFFICES_PATH}/${officeId}/fees`, fee);
    if (!response.success) return null;

    return response.data as Fee;
  }

  static async updateOfficeFee(officeId: string, fee: Fee): Promise<Fee | null> {
    const response = await apiService.post(`${this.OFFICES_PATH}/${officeId}/fees`, fee);
    if (!response.success) return null;

    return response.data as Fee;
  }

  static async duplicateFeeToOffice(officeId: string, feeId: string): Promise<Fee | null> {
    const response = await apiService.post(`${this.OFFICES_PATH}/${officeId}/fees/${feeId}/duplicate`, {});
    if (!response.success) return null;

    return response.data as Fee;
  }

  static async duplicateAllFeeToOffice(officeId: string): Promise<Fee[]> {
    const response = await apiService.post(`${this.OFFICES_PATH}/${officeId}/fees/duplicate-all`, {});
    if (!response.success) return [];

    return response.data as Fee[];
  }

  static async getOfficeArchivedFees(officeId: string, page = 0, size = 200, sort = "name,asc"): Promise<ApiPage<Fee>> {
    const response = await apiService.get(`${this.OFFICES_PATH}/${officeId}/fees/archived?page=${page}&size=${size}&sort=${sort}`);
    if (!response.success) return emptyApiPage<Fee>();

    return response.data as ApiPage<Fee>;
  }

  static async restoreOfficeFee(officeId: string, fee: Fee, archived = false): Promise<Fee | null> {
    const response = await apiService.put(`${this.OFFICES_PATH}/${officeId}/fees/${fee.feeId}`, {
      ...fee,
      archived: archived,
    });
    if (!response.success) return null;

    return response.data as Fee;
  }
}
